import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { EChartsOption } from "echarts";
import { useNavigate, useParams } from "react-router-dom";
import { PageLayout } from "../components/PageLayout.js";
import { QueryResultsGrid } from "../components/QueryResultsGrid.js";
import type { QueryResultsPayload } from "../components/QueryResultsGrid.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { Chart } from "../components/Chart.js";
import { KpiCard } from "../components/KpiCard.js";
import { TanTableViz } from "../components/TanTableViz.js";
import { apiFetch } from "../lib/api.js";
import { onAiChatComposerKeyDown } from "../lib/aiChatComposerKeyDown.js";
import { AI_USAGE_LIMIT_COOLDOWN_MS, isAiUsageLimitError } from "../lib/aiRateLimit.js";
import { fetchExecutionLog, type ExecutionLogEntry } from "../lib/executionLog.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { useToast } from "../components/Toast.js";
import type { TanTableRenderModel } from "../lib/tanTableSpec.js";
import { SpecSnapshotHistoryButton } from "../components/SpecSnapshotHistory.js";

type SourceSpec = {
  specVersion: "source.v1";
  kind: "sql";
  sql: string;
  limit?: number;
};

type TransformSpec = {
  specVersion: "transform.v1";
  output: "table" | "series";
  ops: unknown[];
  series?: unknown;
};

type VLVizSpec = {
  kind?: string;
  specVersion?: string;
  option?: unknown;
  vizType?: string;
  [key: string]: unknown;
};

type PipelineSpec = {
  source: SourceSpec;
  transform: TransformSpec | null;
  viz: VLVizSpec | null;
};

type GridDisplayOptions = {
  rowTotals?: boolean;
  columnTotals?: boolean;
};

type RenderModel =
  | {
      kind: "echarts";
      option: unknown;
      height?: number;
      debugInfo?: unknown;
    }
  | {
      kind: "kpi";
      metrics: Array<{ label: string; formatted: string }>;
      style: Record<string, unknown>;
      detailData?: unknown;
    }
  | TanTableRenderModel;

type Report = {
  id: string;
  name: string;
  description: string | null;
  spec: PipelineSpec;
  last: {
    renderModel: RenderModel | null;
    runAt: string | null;
    durationMs: number | null;
    rowCount: number | null;
    specHash: string | null;
    error: string | null;
  } | null;
  data_provider?: {
    id: string;
    name: string;
    description: string | null;
    connector_id: string | null;
    connector_name: string | null;
    source_spec: SourceSpec;
    last_source_result: QueryResultsPayload | null;
    last_run_at: string | null;
    last_duration_ms: number | null;
    last_row_count: number | null;
    last_attempt_run_at: string | null;
    last_attempt_status: "success" | "failed" | null;
    last_attempt_error_code: string | null;
    last_attempt_error_message: string | null;
  };
};

type EditorTab = "metadata" | "source" | "transform" | "viz";
type SourceSubTab = "cache" | "schema";
type TransformSubTab = "data" | "schema";

type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  text: string;
};

type PersistedChatMessage = {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  assistant_viz_option?: { option?: unknown } | null;
  assistant_transform_spec?: unknown;
};

type AskMessageResponse = {
  assistantMessage: { id: string; displayText: string };
  renderModel: RenderModel | null;
  meta?: {
    persistableOption?: unknown;
    persistableVizSpec?: { specVersion?: string; kind?: string; title?: string; grid?: GridDisplayOptions; option?: unknown; vizType?: string } | Record<string, unknown>;
    vizType?: string;
    responseMode?: "chart" | "grid" | "table" | "tantable" | "clarify" | "chat";
  };
};

type LayerRunStats = {
  runAt: string | null;
  durationMs: number | null;
  rowCount: number | null;
};

const TRANSFORM_DEFAULT_TEMPLATE = {
  specVersion: "transform.v1",
  output: "table",
  ops: []
} as const;

function parseOptionalJson<T>(value: string, errorMessage: string): { value: T | null; error: string | null } {
  const trimmed = value.trim();
  if (trimmed === "") return { value: null, error: null };
  try {
    return { value: JSON.parse(trimmed) as T, error: null };
  } catch {
    return { value: null, error: errorMessage };
  }
}

function normalizeForCompare(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeForCompare(item));
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    const keys = Object.keys(record).sort();
    const out: Record<string, unknown> = {};
    for (const key of keys) {
      out[key] = normalizeForCompare(record[key]);
    }
    return out;
  }
  return value;
}

function equalJsonShape(a: unknown, b: unknown): boolean {
  return JSON.stringify(normalizeForCompare(a)) === JSON.stringify(normalizeForCompare(b));
}

function vizTypeFromSpec(spec: unknown): string {
  if (!spec || typeof spec !== "object" || Array.isArray(spec)) return "unknown";
  const record = spec as Record<string, unknown>;
  if (typeof record.vizType === "string" && record.vizType.trim() !== "") return record.vizType.trim();
  if (record.kind === "tantable" || record.kind === "table" || record.kind === "grid") return "tantable";
  return "unknown";
}

function sourceTransformSnapshot(name: string, description: string, source: SourceSpec, transform: TransformSpec | null): string {
  return JSON.stringify({
    name: name.trim(),
    description: description.trim() || null,
    source,
    transform
  });
}

function stripOptionDataForDisplay(option: unknown): unknown {
  if (!option || typeof option !== "object") return option;
  const cleaned = JSON.parse(JSON.stringify(option)) as Record<string, unknown>;
  const dataset = cleaned.dataset;
  if (Array.isArray(dataset)) {
    const first = dataset[0];
    if (first && typeof first === "object" && !Array.isArray(first)) {
      delete (first as Record<string, unknown>).source;
    }
  } else if (dataset && typeof dataset === "object" && !Array.isArray(dataset)) {
    delete (dataset as Record<string, unknown>).source;
  }

  if (Array.isArray(cleaned.series)) {
    for (const series of cleaned.series) {
      if (series && typeof series === "object" && !Array.isArray(series)) {
        delete (series as Record<string, unknown>).data;
      }
    }
  }
  const stripAxis = (axis: unknown) => {
    if (Array.isArray(axis)) {
      for (const entry of axis) {
        if (entry && typeof entry === "object" && !Array.isArray(entry)) {
          delete (entry as Record<string, unknown>).data;
        }
      }
      return;
    }
    if (axis && typeof axis === "object" && !Array.isArray(axis)) {
      delete (axis as Record<string, unknown>).data;
    }
  };
  stripAxis(cleaned.xAxis);
  stripAxis(cleaned.yAxis);
  return cleaned;
}

function inferFieldType(type: string, sample: unknown): "string" | "number" | "date" {
  const normalized = String(type || "").toLowerCase();
  if (typeof sample === "number") return "number";
  if (sample instanceof Date) return "date";
  if (typeof sample === "string" && !Number.isNaN(Date.parse(sample))) return "date";
  if (normalized.includes("int") || normalized.includes("numeric") || normalized.includes("decimal") || normalized.includes("float") || normalized.includes("double")) {
    return "number";
  }
  if (normalized.includes("date") || normalized.includes("time")) return "date";
  return "string";
}

export function ReportEditor() {
  const params = useParams<{ reportId?: string; visualizationId?: string }>();
  const reportId = params.visualizationId ?? params.reportId ?? "";
  const nav = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [dataProviderId, setDataProviderId] = useState("");
  const [dataProviders, setDataProviders] = useState<Array<{ id: string; name: string }>>([]);
  const [transformJson, setTransformJson] = useState("");
  const [savedSourceTransformSnapshot, setSavedSourceTransformSnapshot] = useState("");
  const [savedVlSpec, setSavedVlSpec] = useState<VLVizSpec | null>(null);
  const [activeTab, setActiveTab] = useState<EditorTab>("metadata");
  const [sourceSubTab, setSourceSubTab] = useState<SourceSubTab>("cache");
  const [transformSubTab, setTransformSubTab] = useState<TransformSubTab>("data");
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [lastRunAt, setLastRunAt] = useState<string | null>(null);
  const [lastDurationMs, setLastDurationMs] = useState<number | null>(null);
  const [lastRowCount, setLastRowCount] = useState<number | null>(null);

  // Per-layer run stats (independent)
  const [sourceStats, setSourceStats] = useState<LayerRunStats | null>(null);
  const [transformStats, setTransformStats] = useState<LayerRunStats | null>(null);
  const [vizStats, setVizStats] = useState<LayerRunStats | null>(null);

  const [transformPreview, setTransformPreview] = useState<QueryResultsPayload | null>(null);
  const [vizPreview, setVizPreview] = useState<RenderModel | null>(null);
  const [stageErrors, setStageErrors] = useState<{ source: string | null; transform: string | null; viz: string | null }>({
    source: null,
    transform: null,
    viz: null
  });
  const [vizErrorBlocksRun, setVizErrorBlocksRun] = useState(false);

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [prompt, setPrompt] = useState("");
  const [sendingPrompt, setSendingPrompt] = useState(false);
  const [latestAskOption, setLatestAskOption] = useState<unknown | null>(null);
  const [showChartConfig, setShowChartConfig] = useState(false);
  const [chartConfigDraft, setChartConfigDraft] = useState("");
  const [chartConfigError, setChartConfigError] = useState<string | null>(null);
  const vizChatScrollRef = useRef<HTMLDivElement>(null);
  const lastVizMessageCountRef = useRef(0);

  // Transform chat state
  const [transformSessionId, setTransformSessionId] = useState<string | null>(null);
  const [transformChatMessages, setTransformChatMessages] = useState<ChatMessage[]>([]);
  const [transformPrompt, setTransformPrompt] = useState("");
  const [sendingTransformPrompt, setSendingTransformPrompt] = useState(false);
  const [aiAssistCooldownUntil, setAiAssistCooldownUntil] = useState(0);
  useEffect(() => {
    if (aiAssistCooldownUntil <= Date.now()) return;
    const ms = aiAssistCooldownUntil - Date.now();
    const id = window.setTimeout(() => setAiAssistCooldownUntil(0), ms);
    return () => window.clearTimeout(id);
  }, [aiAssistCooldownUntil]);
  const aiAssistBlocked = aiAssistCooldownUntil > Date.now();
  const [showTransformConfig, setShowTransformConfig] = useState(false);
  const [transformConfigError, setTransformConfigError] = useState<string | null>(null);
  const transformChatScrollRef = useRef<HTMLDivElement>(null);
  const lastTransformMessageCountRef = useRef(0);
  const [report, setReport] = useState<Report | null>(null);
  const [providerExecutions, setProviderExecutions] = useState<ExecutionLogEntry[]>([]);

  type TenantAiModelRow = {
    model_id: string;
    status: string;
    capabilities?: {
      temperature: boolean;
      max_output_tokens: boolean;
      response_format: boolean;
      web_search: boolean;
      tools: boolean;
    };
  };
  const [tenantAiModels, setTenantAiModels] = useState<TenantAiModelRow[]>([]);
  const [selectedAiModelId, setSelectedAiModelId] = useState("");
  const aiPrefSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const data = await apiFetch<{ models: TenantAiModelRow[]; hub_default: string; user_preference: string | null }>(
          "/api/tenant/ai/models"
        );
        setTenantAiModels(data.models);
        const pref = data.user_preference;
        const validPref = Boolean(pref && data.models.some((m) => m.model_id === pref));
        const pick = (validPref ? pref! : null) ?? data.hub_default ?? data.models[0]?.model_id ?? "";
        setSelectedAiModelId(pick);
      } catch {
        setTenantAiModels([]);
      }
    })();
  }, []);

  const scheduleSaveAiModelPreference = useCallback((modelId: string) => {
    if (aiPrefSaveTimerRef.current) clearTimeout(aiPrefSaveTimerRef.current);
    aiPrefSaveTimerRef.current = setTimeout(() => {
      void apiFetch("/api/tenant/account/preferences", {
        method: "PATCH",
        body: { preferred_ai_model_id: modelId }
      }).catch(() => {});
    }, 600);
  }, []);

  function onTenantAiModelChange(modelId: string) {
    setSelectedAiModelId(modelId);
    scheduleSaveAiModelPreference(modelId);
  }

  const transformParse = useMemo(() => parseOptionalJson<TransformSpec>(transformJson, "Invalid transform JSON"), [transformJson]);

  const selectedDataProvider = useMemo(() => {
    return report?.data_provider ?? null;
  }, [report]);

  const sourceCache = useMemo(() => {
    if (!selectedDataProvider?.last_source_result || !selectedDataProvider.last_run_at) return null;
    return {
      sourceResult: selectedDataProvider.last_source_result,
      runAt: selectedDataProvider.last_run_at,
      durationMs: selectedDataProvider.last_duration_ms ?? null,
      rowCount: selectedDataProvider.last_row_count ?? null
    };
  }, [selectedDataProvider]);

  const currentSpec = useMemo<PipelineSpec | null>(
    () => {
      if (!selectedDataProvider) return null;
      return {
        source: selectedDataProvider.source_spec,
        transform: transformParse.value,
        viz: savedVlSpec
      };
    },
    [selectedDataProvider, transformParse.value, savedVlSpec]
  );

  const dirtySourceTransform = useMemo(() => {
    if (!selectedDataProvider) return false;
    return sourceTransformSnapshot(name, description, selectedDataProvider.source_spec, transformParse.value) !== savedSourceTransformSnapshot;
  }, [name, description, selectedDataProvider, transformParse.value, savedSourceTransformSnapshot]);
  const hasUnappliedChart =
    latestAskOption != null && !equalJsonShape(latestAskOption, savedVlSpec?.option ?? null);
  const runDisabledReason = dirtySourceTransform
    ? "Save source/transform before Full Refresh."
    : vizErrorBlocksRun && (chartConfigError || stageErrors.viz)
      ? `Fix visualization error before Full Refresh: ${chartConfigError ?? stageErrors.viz}`
      : null;
  const runDisabled = busy || sendingPrompt || runDisabledReason !== null;
  const availableFields = useMemo(() => {
    if (!sourceCache?.sourceResult) return [];
    return sourceCache.sourceResult.columns.map((column, idx) => ({
        name: column.name,
      type: inferFieldType(column.type, sourceCache.sourceResult.rows[0]?.[idx])
    }));
  }, [sourceCache]);

  async function loadProviderExecutions(providerId: string) {
    try {
      const entries = await fetchExecutionLog({
        data_provider_id: providerId,
        limit: 20
      });
      setProviderExecutions(entries);
    } catch {
      setProviderExecutions([]);
    }
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [reportData, providersData] = await Promise.all([
        apiFetch<{ visualization: Report }>(`/api/tenant/visualizations/${encodeURIComponent(reportId)}`).catch(() => 
          apiFetch<{ report: Report }>(`/api/tenant/reports/${encodeURIComponent(reportId)}`)
        ),
        apiFetch<{ data_providers: Array<{ id: string; name: string }> }>("/api/tenant/data-providers")
      ]);
      const report = "visualization" in reportData ? reportData.visualization : reportData.report;
      setReport(report);
      setDataProviders(providersData.data_providers);
      const rawTransform = report.spec.transform;
      const isStale = !rawTransform || (rawTransform as { specVersion?: unknown }).specVersion === "transform.v0";
      setName(report.name);
      setDescription(report.description ?? "");
      setDataProviderId(report.data_provider?.id ?? "");
      if (report.data_provider?.id) {
        await loadProviderExecutions(report.data_provider.id);
      } else {
        setProviderExecutions([]);
      }
      setSavedVlSpec(report.spec.viz ?? null);
      const sourceSpec = report.data_provider?.source_spec ?? report.spec.source;
      setLastRunAt(report.last?.runAt ?? null);
      setLastDurationMs(report.last?.durationMs ?? null);
      setLastRowCount(report.last?.rowCount ?? null);
      const initialStats: LayerRunStats | null = report.last
        ? {
            runAt: report.last.runAt ?? null,
            durationMs: report.last.durationMs ?? null,
            rowCount: report.last.rowCount ?? null
          }
        : null;
      setSourceStats(initialStats);
      setTransformStats(initialStats);
      setVizStats(initialStats);
      setVizPreview(report.last?.renderModel ?? null);
      setMetadataError(null);
      setStageErrors({ source: null, transform: null, viz: report.last?.error ?? null });
      // Historical last-run errors should be visible, but should not deadlock reruns.
      setVizErrorBlocksRun(false);
      setPrompt("");
      const [chatData, transformChatData] = await Promise.all([
        apiFetch<{ session: { id: string } | null; messages: PersistedChatMessage[] }>(
          `/api/tenant/ai/viz/sessions/latest?report_id=${encodeURIComponent(reportId)}`
        ).catch(() => ({ session: null, messages: [] })),
        apiFetch<{ session: { id: string } | null; messages: PersistedChatMessage[] }>(
          `/api/tenant/ai/transform/sessions/latest?report_id=${encodeURIComponent(reportId)}`
        ).catch(() => ({ session: null, messages: [] }))
      ]);
      setSessionId(chatData.session?.id ?? null);
      const visibleMessages = chatData.messages
        .filter((entry) => entry.role === "user" || entry.role === "assistant")
        .map((entry) => ({
          id: entry.id,
          role: entry.role as "user" | "assistant",
          text: entry.content
        }));
      setChatMessages(visibleMessages);
      const lastAssistantWithOption = [...chatData.messages]
        .reverse()
        .find((entry) => entry.role === "assistant" && entry.assistant_viz_option?.option !== undefined);
      setLatestAskOption(lastAssistantWithOption?.assistant_viz_option?.option ?? null);

      setTransformSessionId(transformChatData.session?.id ?? null);
      const transformVisibleMessages = transformChatData.messages
        .filter((entry) => entry.role === "user" || entry.role === "assistant")
        .map((entry) => ({
          id: entry.id,
          role: entry.role as "user" | "assistant",
          text: entry.content
        }));
      setTransformChatMessages(transformVisibleMessages);

      // Determine the final transform spec — must happen AFTER AI history is loaded so we can
      // pick the best AI spec if the persisted spec is stale (null / transform.v0).
      // Then set BOTH transformJson and savedSourceTransformSnapshot from the same value so dirtySourceTransform
      // starts as false and the viz AI Send button is enabled on page load.
      let finalTransform: TransformSpec = isStale ? (TRANSFORM_DEFAULT_TEMPLATE as unknown as TransformSpec) : (rawTransform as TransformSpec);
      if (isStale) {
        const lastTransformAssistant = [...transformChatData.messages]
          .reverse()
          .find((entry) => entry.role === "assistant" && entry.assistant_transform_spec);
        if (lastTransformAssistant?.assistant_transform_spec) {
          finalTransform = lastTransformAssistant.assistant_transform_spec as TransformSpec;
        }
      }
      setTransformJson(JSON.stringify(finalTransform, null, 2));
      setSavedSourceTransformSnapshot(sourceTransformSnapshot(report.name, report.description ?? "", sourceSpec, finalTransform));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!reportId) return;
    void load();
  }, [reportId]);

  // After every load cycle, auto-run transform if source cache exists and the spec has real ops.
  // This ensures the schema contract and data tabs are always populated without requiring
  // a manual "Run Transformation" click.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!loading && sourceCache?.sourceResult && transformParse.value && (transformParse.value.ops?.length ?? 0) > 0) {
      void previewStage("transform");
    }
  }, [loading]); // intentionally only on loading — not on every sourceCache/transformParse change

  // Auto-scroll viz chat to bottom on new messages
  useEffect(() => {
    if (vizChatScrollRef.current) {
      vizChatScrollRef.current.scrollTop = vizChatScrollRef.current.scrollHeight;
    }
    const hasNewMessage = chatMessages.length > lastVizMessageCountRef.current;
    lastVizMessageCountRef.current = chatMessages.length;
    if (hasNewMessage) {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
      });
    }
  }, [chatMessages]);

  // Auto-scroll transform chat to bottom on new messages
  useEffect(() => {
    if (transformChatScrollRef.current) {
      transformChatScrollRef.current.scrollTop = transformChatScrollRef.current.scrollHeight;
    }
    const hasNewMessage = transformChatMessages.length > lastTransformMessageCountRef.current;
    lastTransformMessageCountRef.current = transformChatMessages.length;
    if (hasNewMessage) {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
      });
    }
  }, [transformChatMessages]);

  async function duplicateReport() {
    setBusy(true);
    try {
      const data = await apiFetch<{ report: { id: string } }>(`/api/tenant/reports/${encodeURIComponent(reportId)}/duplicate`, {
        method: "POST",
        body: {}
      });
      nav(`/app/build/visualizations/${data.report.id}`);
    } catch (e) {
      setMetadataError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function deleteReport() {
    const ok = await confirm.confirm({
      title: "Delete report?",
      detail: "This cannot be undone.",
      confirmLabel: "Delete"
    });
    if (!ok) return;
    setBusy(true);
    try {
      await apiFetch(`/api/tenant/reports/${encodeURIComponent(reportId)}`, { method: "DELETE" });
      nav("/app/build/visualizations");
    } catch (e) {
      setMetadataError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function previewStage(stage: "transform", specOverride?: TransformSpec) {
    // specOverride allows callers (e.g. sendTransformPrompt) to pass a freshly-received spec
    // before React has re-rendered with the updated transformJson state.
    if (!specOverride && transformParse.error) {
      setStageErrors((current) => ({ ...current, transform: transformParse.error }));
      return;
    }
    if (!selectedDataProvider) {
      setStageErrors((current) => ({ ...current, transform: "Select a data provider first" }));
      return;
    }
    
    setStageErrors((current) => ({ ...current, [stage]: null }));
    setBusy(true);
    try {
      // specOverride wins when provided (e.g. fresh AI spec before React re-renders).
      // Fall back to parsed transformJson, then passthrough if neither is available.
      const effectiveTransform: TransformSpec = specOverride ?? transformParse.value ?? ({ specVersion: "transform.v1", output: "table", ops: [] } as TransformSpec);
      const payload = await apiFetch<{
        sourceResult?: QueryResultsPayload;
        transformResult?: { kind: "table"; table: { columns: Array<{ name: string; type: string }>; rows: unknown[][]; rowCount: number } };
        meta?: { fromCache?: boolean; durationMs?: number; rowCount?: number; runAt?: string };
      }>("/api/tenant/visualization-runtime/preview", {
        method: "POST",
        body: {
          stage,
          visualization_id: reportId,
          draft: {
            visualization: {
              spec_transform: effectiveTransform,
              spec_viz: savedVlSpec
            }
          }
        }
      });
      if (payload.transformResult?.kind === "table") {
        const tlRunAt = payload.meta?.runAt ?? new Date().toISOString();
        const tlDurationMs = payload.meta?.durationMs ?? null;
        const transformRowCount = payload.transformResult.table.rowCount ?? payload.meta?.rowCount ?? null;
        setTransformPreview({
          columns: payload.transformResult.table.columns,
          rows: payload.transformResult.table.rows,
          rowCount: payload.transformResult.table.rowCount,
          durationMs: 0
        });
        setTransformStats({
          runAt: tlRunAt,
          durationMs: tlDurationMs,
          rowCount: transformRowCount
        });
        setTransformSubTab("data");
      }
      setActiveTab(stage);
    } catch (e) {
      setStageErrors((current) => ({ ...current, [stage]: e instanceof Error ? e.message : String(e) }));
    } finally {
      setBusy(false);
    }
  }

  async function ensureSession(): Promise<string> {
    if (sessionId) return sessionId;
    const data = await apiFetch<{ session: { id: string } }>("/api/tenant/ai/viz/sessions", {
      method: "POST",
      body: { report_id: reportId }
    });
    setSessionId(data.session.id);
    return data.session.id;
  }

  async function sendAskPrompt() {
    const message = prompt.trim();
    if (!message) return;
    if (dirtySourceTransform) {
      setStageErrors((current) => ({ ...current, viz: "Save source/transform to use Ask Atlas." }));
        return;
      }
    setSendingPrompt(true);
          setStageErrors((current) => ({ ...current, viz: null }));
    setVizErrorBlocksRun(false);
    setChatMessages((current) => [...current, { id: `local-user-${Date.now()}`, role: "user", text: message }]);
    setPrompt("");
    try {
      const sid = await ensureSession();
      const data = await apiFetch<AskMessageResponse>(`/api/tenant/ai/viz/sessions/${encodeURIComponent(sid)}/messages`, {
        method: "POST",
        body: {
          message,
          viz_type: vizTypeFromSpec(savedVlSpec),
          // Pass the current transform preview so the AI uses the same data the user sees,
          // not a re-execution of the stored (potentially stale) pipeline.
          transformResult: transformPreview
            ? { kind: "table", table: { columns: transformPreview.columns, rows: transformPreview.rows, rowCount: transformPreview.rowCount } }
            : undefined,
          ...(selectedAiModelId ? { model: selectedAiModelId } : {})
        }
      });
      setChatMessages((current) => [...current, { id: data.assistantMessage.id, role: "assistant", text: data.assistantMessage.displayText }]);
      if (data.renderModel) {
        setVizPreview(data.renderModel);
      }
      const persistableVizSpec = data.meta?.persistableVizSpec;
      const persistableOption = data.meta?.persistableOption;
      const selectedVizType = data.meta?.vizType;
      if (persistableVizSpec) {
        // Tabular viz (tantable / legacy table or grid JSON)
        setLatestAskOption(null);
        // Update config draft if panel is open
        if (showChartConfig) {
          setChartConfigDraft(JSON.stringify(persistableVizSpec, null, 2));
          setChartConfigError(null);
        }
        try {
          const persistData = await apiFetch<{ report: Report }>(`/api/tenant/reports/${encodeURIComponent(reportId)}/viz`, {
            method: "POST",
            body: {
              spec_viz: {
                ...persistableVizSpec,
                ...(selectedVizType ? { vizType: selectedVizType } : {})
              }
            }
          });
          setSavedVlSpec(persistData.report.spec.viz ?? null);
        } catch (e) {
          console.warn("[ReportEditor] Failed to auto-save AI-generated viz spec:", e);
        }
      } else if (persistableOption !== undefined) {
        // Chart report — unchanged
        setLatestAskOption(persistableOption);
        // Update config draft if panel is open
        if (showChartConfig) {
          loadChartConfigDraft(persistableOption);
        }
        // Auto-save the AI-generated chart option so it persists when navigating away
        try {
          const persistData = await apiFetch<{ report: Report }>(`/api/tenant/reports/${encodeURIComponent(reportId)}/viz`, {
            method: "POST",
            body: {
              spec_viz: persistableVizSpec ?? {
                option: persistableOption,
                ...(selectedVizType ? { vizType: selectedVizType } : {})
              }
            }
          });
          setSavedVlSpec(persistData.report.spec.viz ?? null);
        } catch (e) {
          // Log error but don't block the UI - the option is still in latestAskOption
          console.warn("[ReportEditor] Failed to auto-save AI-generated chart option:", e);
        }
      }
          setActiveTab("viz");
        } catch (e) {
          if (isAiUsageLimitError(e)) {
            toast.push({ kind: "error", message: e.message });
            setAiAssistCooldownUntil(Date.now() + AI_USAGE_LIMIT_COOLDOWN_MS);
            setPrompt(message);
            setChatMessages((c) =>
              c.length > 0 && c[c.length - 1]?.id.startsWith("local-user-") ? c.slice(0, -1) : c
            );
          } else {
            setStageErrors((current) => ({ ...current, viz: e instanceof Error ? e.message : String(e) }));
          }
    } finally {
      setSendingPrompt(false);
    }
  }

  function loadChartConfigDraft(option: unknown) {
    setChartConfigDraft(JSON.stringify(stripOptionDataForDisplay(option), null, 2));
    setChartConfigError(null);
  }

  function resetChat() {
    setSessionId(null);
    setChatMessages([]);
    setLatestAskOption(null);
    setPrompt("");
    setStageErrors((current) => ({ ...current, viz: null }));
    setVizErrorBlocksRun(false);
  }

  async function ensureTransformSession(): Promise<string> {
    if (transformSessionId) return transformSessionId;
    const data = await apiFetch<{ session: { id: string } }>("/api/tenant/ai/transform/sessions", {
      method: "POST",
      body: { report_id: reportId }
    });
    setTransformSessionId(data.session.id);
    return data.session.id;
  }

  async function sendTransformPrompt() {
    const message = transformPrompt.trim();
    if (!message) return;
    if (dirtySourceTransform) {
      setStageErrors((current) => ({ ...current, transform: "Save source/transform to use Ask Atlas." }));
      return;
    }
    setSendingTransformPrompt(true);
    setStageErrors((current) => ({ ...current, transform: null }));
    setTransformChatMessages((current) => [...current, { id: `local-user-${Date.now()}`, role: "user", text: message }]);
    setTransformPrompt("");
    try {
      const sid = await ensureTransformSession();
      const currentSpec: TransformSpec = transformParse.value ?? ({ specVersion: "transform.v1", output: "table", ops: [] } as TransformSpec);
      const data = await apiFetch<{ assistantMessage: { id: string; displayText: string }; transformSpec?: TransformSpec; meta?: { persistableTransformSpec?: TransformSpec } }>(
        `/api/tenant/ai/transform/sessions/${encodeURIComponent(sid)}/messages`,
        {
          method: "POST",
          body: { message, currentTransformSpec: currentSpec, ...(selectedAiModelId ? { model: selectedAiModelId } : {}) }
        }
      );
      setTransformChatMessages((current) => [...current, { id: data.assistantMessage.id, role: "assistant", text: data.assistantMessage.displayText }]);

      const newSpec: TransformSpec | null = data.meta?.persistableTransformSpec ?? data.transformSpec ?? null;

      if (newSpec) {
        // Update editor immediately — use the spec directly before React re-renders
        setTransformJson(JSON.stringify(newSpec, null, 2));
        // Run transformation with the fresh spec now, before transformParse catches up
        void previewStage("transform", newSpec);
        // Persist to DB in background
        try {
          await apiFetch(`/api/tenant/visualizations/${encodeURIComponent(reportId)}`, {
            method: "PATCH",
            body: { spec_transform: newSpec }
          }).catch(() =>
            apiFetch(`/api/tenant/reports/${encodeURIComponent(reportId)}`, {
              method: "PATCH",
              body: { spec: { transform: newSpec } }
            })
          );
          setSavedSourceTransformSnapshot(sourceTransformSnapshot(name, description, selectedDataProvider?.source_spec ?? { specVersion: "source.v1", kind: "sql", sql: "" }, newSpec));
        } catch (e) {
          console.warn("[ReportEditor] Failed to auto-save AI-generated transform spec:", e);
        }
      }
      setActiveTab("transform");
    } catch (e) {
      if (isAiUsageLimitError(e)) {
        toast.push({ kind: "error", message: e.message });
        setAiAssistCooldownUntil(Date.now() + AI_USAGE_LIMIT_COOLDOWN_MS);
        setTransformPrompt(message);
        setTransformChatMessages((c) =>
          c.length > 0 && c[c.length - 1]?.id.startsWith("local-user-") ? c.slice(0, -1) : c
        );
      } else {
        setStageErrors((current) => ({ ...current, transform: e instanceof Error ? e.message : String(e) }));
      }
    } finally {
      setSendingTransformPrompt(false);
    }
  }

  function resetTransformChat() {
    setTransformSessionId(null);
    setTransformChatMessages([]);
    setTransformPrompt("");
  }


  async function handleTransformConfigBlur() {
    // Validate on blur. If valid, persist to DB silently. If invalid, show inline error.
    const parsed = parseOptionalJson<TransformSpec>(transformJson, "Invalid transformation config JSON");
    if (parsed.error) {
      setTransformConfigError(parsed.error);
      return;
    }
    setTransformConfigError(null);
    try {
      await apiFetch(`/api/tenant/visualizations/${encodeURIComponent(reportId)}`, {
        method: "PATCH",
        body: { spec_transform: parsed.value }
      }).catch(() =>
        apiFetch(`/api/tenant/reports/${encodeURIComponent(reportId)}`, {
          method: "PATCH",
          body: { spec: { transform: parsed.value } }
        })
      );
      setSavedSourceTransformSnapshot(
        sourceTransformSnapshot(name, description, selectedDataProvider?.source_spec ?? { specVersion: "source.v1", kind: "sql", sql: "" }, parsed.value)
      );
    } catch (e) {
      console.warn("[ReportEditor] Failed to auto-save transform config on blur:", e);
    }
  }

  async function handleChartConfigBlur() {
    // Validate on blur. If valid, update preview and persist to DB. If invalid, show inline error.
    try {
      setChartConfigError(null);
      const parsed = JSON.parse(chartConfigDraft) as Record<string, unknown>;
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        setChartConfigError("Visualization config must be a JSON object.");
        return;
      }
      if (!selectedDataProvider) {
        setChartConfigError("Select a data provider first");
        return;
      }

      // Full viz spec object vs bare ECharts option
      const isGridSpec = parsed.specVersion === "viz.v0" && parsed.kind === "grid";
      const isTableSpec = parsed.specVersion === "table.v1" && parsed.kind === "table";
      const isTanTableSpec = parsed.specVersion === "tantable.v1" && parsed.kind === "tantable";
      const currentVizType = vizTypeFromSpec(savedVlSpec);
      const specVizPayload = isGridSpec || isTableSpec || isTanTableSpec
        ? parsed
        : {
            option: parsed,
            ...(currentVizType !== "unknown" ? { vizType: currentVizType } : {})
          };

      // Update preview
      const effectiveTransform: TransformSpec = transformParse.value ?? ({ specVersion: "transform.v1", output: "table", ops: [] } as TransformSpec);
      const preview = await apiFetch<{ renderModel: RenderModel; meta?: { fromCache?: boolean } }>(
        "/api/tenant/visualization-runtime/preview",
        {
          method: "POST",
          body: {
            stage: "viz",
            visualization_id: reportId,
            transformResult: transformPreview
              ? { kind: "table", table: { columns: transformPreview.columns, rows: transformPreview.rows, rowCount: transformPreview.rowCount } }
              : undefined,
            draft: {
              visualization: {
                spec_transform: effectiveTransform,
                spec_viz: specVizPayload
              }
            }
          }
        }
      );
      if (isGridSpec || isTableSpec || isTanTableSpec) {
        setLatestAskOption(null);
      } else {
        setLatestAskOption(parsed);
      }
      setVizPreview(preview.renderModel);
      setStageErrors((current) => ({ ...current, viz: null }));
      setVizErrorBlocksRun(false);
      // Persist to DB
      try {
        const data = await apiFetch<{ report: Report }>(`/api/tenant/reports/${encodeURIComponent(reportId)}/viz`, {
          method: "POST",
          body: { spec_viz: specVizPayload }
        });
        setSavedVlSpec(data.report.spec.viz ?? null);
      } catch (e) {
        console.warn("[ReportEditor] Failed to auto-save visualization config on blur:", e);
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      if (message.toLowerCase().includes("json")) {
        setChartConfigError("Invalid visualization config JSON.");
        return;
      }
      setChartConfigError(message);
    }
  }

  async function handleVizSpecRestore(spec: unknown) {
    // Called after a snapshot restore — the DB was already updated by the restore endpoint.
    // We need to update local state and re-run the viz preview so the panel reflects the restored spec.
    const specObj = (spec ?? {}) as VLVizSpec;
    const json = JSON.stringify(specObj, null, 2);
    setSavedVlSpec(specObj);
    setChartConfigDraft(json);
    setChartConfigError(null);
    if (!selectedDataProvider) return;
    try {
      const effectiveTransform: TransformSpec = transformParse.value ?? ({ specVersion: "transform.v1", output: "table", ops: [] } as TransformSpec);
      const preview = await apiFetch<{ renderModel: RenderModel }>(
        "/api/tenant/visualization-runtime/preview",
        {
          method: "POST",
          body: {
            stage: "viz",
            visualization_id: reportId,
            transformResult: transformPreview
              ? { kind: "table", table: { columns: transformPreview.columns, rows: transformPreview.rows, rowCount: transformPreview.rowCount } }
              : undefined,
            draft: {
              visualization: {
                spec_transform: effectiveTransform,
                spec_viz: specObj
              }
            }
          }
        }
      );
      setVizPreview(preview.renderModel);
      setStageErrors((current) => ({ ...current, viz: null }));
    } catch (e) {
      console.warn("[ReportEditor] Failed to re-render viz after snapshot restore:", e);
    }
  }

  async function handleNameBlur() {
    if (!name.trim()) {
      setMetadataError("Name is required");
      return;
    }
    if (!dataProviderId.trim()) {
      setMetadataError("Data provider is required");
      return;
    }
    setMetadataError(null);
    try {
      await apiFetch(`/api/tenant/visualizations/${encodeURIComponent(reportId)}`, {
        method: "PATCH",
        body: {
          name: name.trim(),
          description: description.trim() || null,
          data_provider_id: dataProviderId.trim(),
          spec_transform: transformParse.value,
          spec_viz: savedVlSpec
        }
      }).catch(() =>
        apiFetch(`/api/tenant/reports/${encodeURIComponent(reportId)}`, {
          method: "PATCH",
          body: {
            name: name.trim(),
            description: description.trim() || null,
            spec: currentSpec
          }
        })
      );
      if (selectedDataProvider) {
        setSavedSourceTransformSnapshot(sourceTransformSnapshot(name, description, selectedDataProvider.source_spec, transformParse.value));
      }
    } catch (e) {
      setMetadataError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleDescriptionBlur() {
    if (!name.trim()) {
      // Don't save if name is invalid
      return;
    }
    if (!dataProviderId.trim()) {
      setMetadataError("Data provider is required");
      return;
    }
    setMetadataError(null);
    try {
      await apiFetch(`/api/tenant/visualizations/${encodeURIComponent(reportId)}`, {
        method: "PATCH",
        body: {
          name: name.trim(),
          description: description.trim() || null,
          data_provider_id: dataProviderId.trim(),
          spec_transform: transformParse.value,
          spec_viz: savedVlSpec
        }
      }).catch(() =>
        apiFetch(`/api/tenant/reports/${encodeURIComponent(reportId)}`, {
          method: "PATCH",
          body: {
            name: name.trim(),
            description: description.trim() || null,
            spec: currentSpec
          }
        })
      );
      if (selectedDataProvider) {
        setSavedSourceTransformSnapshot(sourceTransformSnapshot(name, description, selectedDataProvider.source_spec, transformParse.value));
      }
    } catch (e) {
      setMetadataError(e instanceof Error ? e.message : String(e));
    }
  }

  async function rebuildChartFromCache() {
    await runAndPersist("chart");
  }

  async function runAndPersist(mode: "full" | "chart" = "full") {
    if (dirtySourceTransform) {
      setStageErrors((current) => ({ ...current, viz: "Save source/transform before Full Refresh." }));
      return;
    }
    setBusy(true);
    setMetadataError(null);
    setStageErrors((current) => ({ ...current, viz: null }));
    setVizErrorBlocksRun(false);
    try {
      if (hasUnappliedChart && latestAskOption) {
        const persistData = await apiFetch<{ report: Report }>(`/api/tenant/reports/${encodeURIComponent(reportId)}/viz`, {
          method: "POST",
          body: {
            spec_viz: {
              option: latestAskOption
            }
          }
        });
        setSavedVlSpec(persistData.report.spec.viz ?? null);
      }

      const data = await apiFetch<{
        renderModel: RenderModel;
        meta: {
          durationMs: number;
          rowCount: number;
          runAt: string;
          sourceResult?: QueryResultsPayload;
          transformResult?: { kind: "table"; table: { columns: Array<{ name: string; type: string }>; rows: unknown[][]; rowCount: number } };
          stats?: {
            source: { durationMs: number; cached: boolean };
            transform: { durationMs: number; cached: boolean };
            viz: { durationMs: number };
          };
        }
      }>(
        "/api/tenant/report-runtime/run",
        {
          method: "POST",
          body: {
            report_id: reportId,
            sourceResult: mode === "chart" ? sourceCache?.sourceResult ?? undefined : undefined,
            transformResult:
              mode === "chart" && transformPreview
                ? {
                    kind: "table",
                    table: {
                      columns: transformPreview.columns,
                      rows: transformPreview.rows,
                      rowCount: transformPreview.rowCount
                    }
                  }
                : undefined
          }
        }
      );
      // ── Derive reliable row count ──
      // meta.rowCount is always set server-side (transform output row count).
      // Use it as the single source of truth. Nested sourceResult/transformResult may be
      // absent if the response was large or fields were stripped.
      const serverRowCount = data.meta.rowCount ?? 0;
      const sourceRowCount = data.meta.sourceResult?.rowCount ?? serverRowCount;
      const transformRowCount =
        data.meta.transformResult?.kind === "table"
          ? (data.meta.transformResult.table.rowCount ?? serverRowCount)
          : serverRowCount;

      setVizPreview(data.renderModel);
      setLastRunAt(data.meta.runAt);
      setLastDurationMs(data.meta.durationMs);
      setLastRowCount(serverRowCount);

      // ── Per-layer stats ──
      if (data.meta.stats) {
        const runAt = data.meta.runAt;

        // Full refresh: always update all stats.
        // Chart refresh: only update layers that actually ran (not cached).
        if (!data.meta.stats.source.cached) {
          setSourceStats({ runAt, durationMs: data.meta.stats.source.durationMs, rowCount: sourceRowCount });
        }
        // else: skipped — keep prior sourceStats untouched

        if (!data.meta.stats.transform.cached) {
          setTransformStats({ runAt, durationMs: data.meta.stats.transform.durationMs, rowCount: transformRowCount });
        }
        // else: skipped — keep prior transformStats untouched

        // Viz always runs
        setVizStats({ runAt, durationMs: data.meta.stats.viz.durationMs, rowCount: serverRowCount });
      }

      // ── Cache source/transform in memory for iteration ──
      if (data.meta.sourceResult) {
        // Source cache is now managed via data provider
      }
      if (data.meta.transformResult?.kind === "table") {
        setTransformPreview({
          columns: data.meta.transformResult.table.columns ?? [],
          rows: data.meta.transformResult.table.rows ?? [],
          rowCount: data.meta.transformResult.table.rowCount ?? 0,
          durationMs: 0
        });
      }

      setActiveTab("viz");
    } catch (e) {
      setStageErrors((current) => ({ ...current, viz: e instanceof Error ? e.message : String(e) }));
      setVizErrorBlocksRun(true);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingState />;
  if (error) return <ErrorState detail={error} />;

  return (
    <PageLayout
      title={
        <span className="breadcrumbTitle">
          <button className="breadcrumbLink" type="button" onClick={() => nav("/app/build/visualizations")}>
            Visualizations
          </button>
          <span className="breadcrumbSep">/</span>
          <span>{name || "Visualization"}</span>
        </span>
      }
      description={description || undefined}
      actions={
        <>
          <button className="btn" type="button" onClick={() => void duplicateReport()} disabled={busy || sendingPrompt}>
            Duplicate
          </button>
          <button className="btn" type="button" onClick={() => void deleteReport()} disabled={busy || sendingPrompt}>
            Delete
          </button>
        </>
      }
    >
      <div className="reportEditorGrid">
        <section className="card reportEditorLeft">
          <div className="reportTabs" role="tablist" aria-label="Report editor sections">
            <button className={`reportTabBtn${activeTab === "metadata" ? " reportTabBtnActive" : ""}`} type="button" onClick={() => setActiveTab("metadata")}>
              Metadata
            </button>
            <button className={`reportTabBtn${activeTab === "source" ? " reportTabBtnActive" : ""}`} type="button" onClick={() => setActiveTab("source")}>
              Data Provider
            </button>
            <button className={`reportTabBtn${activeTab === "transform" ? " reportTabBtnActive" : ""}`} type="button" onClick={() => setActiveTab("transform")}>
              Transformation
            </button>
            <button className={`reportTabBtn${activeTab === "viz" ? " reportTabBtnActive" : ""}`} type="button" onClick={() => setActiveTab("viz")}>
              Visualization
            </button>
          </div>

          {activeTab === "metadata" ? (
            <div className="reportSection">
              <h3 className="reportSectionTitle">Metadata</h3>
              <div className="field">
                <label className="label" htmlFor="report-name">
                  Name
                </label>
                <input id="report-name" className="input" value={name} onChange={(e) => setName(e.target.value)} onBlur={() => void handleNameBlur()} />
              </div>
              <div className="field">
                <label className="label" htmlFor="report-description">
                  Description
                </label>
                <textarea id="report-description" className="input reportTextarea" value={description} onChange={(e) => setDescription(e.target.value)} onBlur={() => void handleDescriptionBlur()} />
              </div>
              {metadataError ? <ErrorState detail={metadataError} /> : null}
            </div>
          ) : null}

          {activeTab === "source" ? (
            <div className="reportSection">
              <h3 className="reportSectionTitle">Data Provider</h3>
              <div style={{ marginBottom: "16px" }}>
                <label className="label">Select Data Provider</label>
                <select
                  className="input"
                  value={dataProviderId}
                  onChange={async (e) => {
                    const newProviderId = e.target.value;
                    setDataProviderId(newProviderId);
                    if (newProviderId) {
                      // Load the selected data provider's details
                      try {
                        const providerData = await apiFetch<{ data_provider: Report["data_provider"] }>(
                          `/api/tenant/data-providers/${encodeURIComponent(newProviderId)}`
                        );
                        setReport((prev) => prev ? { ...prev, data_provider: providerData.data_provider } : null);
                        await loadProviderExecutions(newProviderId);
                      } catch (err) {
                        toast.push({ kind: "error", message: err instanceof Error ? err.message : String(err) });
                      }
                    }
                  }}
                  disabled={busy}
                >
                  <option value="">-- Select a data provider --</option>
                  {dataProviders.map((provider) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name}
                    </option>
                  ))}
                </select>
              </div>
              {selectedDataProvider ? (
                <div className="queryMeta" style={{ marginBottom: "16px" }}>
                  {selectedDataProvider.last_attempt_status ? (
                    <span className={`pill ${selectedDataProvider.last_attempt_status === "failed" ? "pillBad" : "pillOk"}`}>
                      Latest attempt: {selectedDataProvider.last_attempt_status}
                    </span>
                  ) : null}
                  {selectedDataProvider.last_attempt_run_at ? (
                    <span className="pill">Attempt time: {new Date(selectedDataProvider.last_attempt_run_at).toLocaleString()}</span>
                  ) : null}
                </div>
              ) : null}
              {selectedDataProvider?.last_attempt_status === "failed" ? (
                <div className="muted" style={{ marginBottom: "12px" }}>
                  {selectedDataProvider.last_attempt_error_code ?? "provider_refresh_failed"}
                  {selectedDataProvider.last_attempt_error_message ? `: ${selectedDataProvider.last_attempt_error_message}` : ""}
                </div>
              ) : null}
              {selectedDataProvider && sourceCache?.sourceResult ? (
                <>
                  <div className="queryMeta" style={{ marginBottom: "16px" }}>
                    {sourceCache.runAt ? <span className="pill">Last run: {new Date(sourceCache.runAt).toLocaleString()}</span> : null}
                    {sourceCache.durationMs != null ? <span className="pill">Duration: {sourceCache.durationMs} ms</span> : null}
                    {sourceCache.rowCount != null ? <span className="pill">Rows: {sourceCache.rowCount.toLocaleString()}</span> : null}
                  </div>
                  <div style={{ display: "flex", gap: "8px", marginBottom: "16px", borderBottom: "1px solid var(--border)" }}>
                    <button
                      type="button"
                      onClick={() => setSourceSubTab("cache")}
                      style={{
                        padding: "8px 16px",
                        border: "none",
                        background: sourceSubTab === "cache" ? "var(--bg-secondary)" : "transparent",
                        borderBottom: sourceSubTab === "cache" ? "2px solid var(--primary)" : "2px solid transparent",
                        cursor: "pointer",
                        fontWeight: sourceSubTab === "cache" ? "600" : "400"
                      }}
                    >
                      Cache
                    </button>
                    <button
                      type="button"
                      onClick={() => setSourceSubTab("schema")}
                      style={{
                        padding: "8px 16px",
                        border: "none",
                        background: sourceSubTab === "schema" ? "var(--bg-secondary)" : "transparent",
                        borderBottom: sourceSubTab === "schema" ? "2px solid var(--primary)" : "2px solid transparent",
                        cursor: "pointer",
                        fontWeight: sourceSubTab === "schema" ? "600" : "400"
                      }}
                    >
                      Schema Contract
                    </button>
                  </div>
                  {sourceSubTab === "cache" ? (
                    <QueryResultsGrid result={sourceCache.sourceResult} hideMeta />
                  ) : null}
                  {sourceSubTab === "schema" ? (
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Column Name</th>
                          <th>Type</th>
                          <th>Domain</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sourceCache.sourceResult.columns.map((column) => (
                          <tr key={column.name}>
                            <td>{column.name}</td>
                            <td>{column.rawType ?? column.type}</td>
                            <td>{column.type}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : null}
                </>
              ) : selectedDataProvider ? (
                <div className="muted" style={{ marginTop: "16px" }}>No cache available. Execute the data provider to generate cache.</div>
              ) : null}
              {selectedDataProvider ? (
                <div style={{ marginTop: "16px" }}>
                  <div className="reportSectionHeader">
                    <h4 className="reportSectionTitle">Execution History</h4>
                  </div>
                  {providerExecutions.length === 0 ? (
                    <div className="muted">No executions yet.</div>
                  ) : (
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Status</th>
                          <th>Rows</th>
                          <th>Duration</th>
                          <th>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {providerExecutions.map((run) => (
                          <tr key={run.id}>
                            <td>{new Date(run.finished_at).toLocaleString()}</td>
                            <td>{run.status}</td>
                            <td>{run.row_count != null ? run.row_count.toLocaleString() : "-"}</td>
                            <td>{run.duration_ms} ms</td>
                            <td>
                              {run.error ?? ""}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ) : null}
            </div>
          ) : null}

          {activeTab === "transform" ? (
            <div className="reportSection">
              <div className="reportSectionHeader">
                <h3 className="reportSectionTitle">Ask Atlas (Transformation)</h3>
                <div className="toolbarGroup">
                  <button className="btn btnSm" type="button" onClick={resetTransformChat} disabled={busy || sendingTransformPrompt || aiAssistBlocked}>
                    Reset chat
                  </button>
                </div>
              </div>
              {dirtySourceTransform ? <div className="muted">Save source/transform to use Ask Atlas.</div> : null}
              <div className="askChatPanel">
                {tenantAiModels.length > 0 ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <select
                      className="pillSelect pillSelectNeutral"
                      value={selectedAiModelId}
                      onChange={(e) => onTenantAiModelChange(e.target.value)}
                      disabled={sendingTransformPrompt || busy || aiAssistBlocked}
                      title="Model for this workspace"
                    >
                      {tenantAiModels.map((m) => (
                        <option key={m.model_id} value={m.model_id}>
                          {m.model_id}
                          {m.status === "deprecated" ? " (deprecated)" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                <div className="askChatMessages" ref={transformChatScrollRef}>
                  {transformChatMessages.map((item) => (
                    <div key={item.id} className={`askChatMessage askChatMessage${item.role === "user" ? "User" : "Assistant"}`}>
                      {item.text}
                    </div>
                  ))}
                </div>
                <div className="askChatComposer">
                  <textarea
                    className="input askChatInput"
                    value={transformPrompt}
                    onChange={(e) => setTransformPrompt(e.target.value)}
                    onKeyDown={(e) =>
                      onAiChatComposerKeyDown(e, {
                        canSubmit:
                          !sendingTransformPrompt &&
                          !busy &&
                          !dirtySourceTransform &&
                          !!transformPrompt.trim() &&
                          !aiAssistBlocked,
                        onSubmit: () => void sendTransformPrompt()
                      })
                    }
                    rows={2}
                    placeholder=""
                    disabled={aiAssistBlocked}
                  />
                  <button
                    className="btn btnPrimary"
                    type="button"
                    onClick={() => void sendTransformPrompt()}
                    disabled={
                      sendingTransformPrompt || busy || dirtySourceTransform || transformPrompt.trim() === "" || aiAssistBlocked
                    }
                  >
                    {sendingTransformPrompt ? "Sending..." : aiAssistBlocked ? "Limit…" : "Send"}
                  </button>
                </div>
              </div>
              <div className="askConfig">
                <button
                  className="btn btnSm"
                  type="button"
                  onClick={() => setShowTransformConfig((v) => !v)}
                >
                  {showTransformConfig ? "Hide Transformation Config" : "Show Transformation Config"}
                </button>
                {showTransformConfig ? (
                  <>
                    <textarea
                      className="input reportJsonEditor"
                      value={transformJson}
                      onChange={(e) => {
                        setTransformJson(e.target.value);
                        setTransformConfigError(null);
                      }}
                      onBlur={() => void handleTransformConfigBlur()}
                      spellCheck={false}
                    />
                    <div className="toolbarGroup" style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
                      <button className="btn btnSm" type="button" onClick={() => void previewStage("transform")} disabled={busy || sendingTransformPrompt || !!transformParse.error}>
                        Run Transformation
                      </button>
                    </div>
                    {transformConfigError ? <ErrorState detail={transformConfigError} /> : null}
                  </>
                ) : null}
              </div>
            </div>
          ) : null}

          {activeTab === "viz" ? (
            <div className="reportSection">
              <div className="reportSectionHeader">
                <h3 className="reportSectionTitle">Ask Atlas (Visualization)</h3>
                <div className="toolbarGroup">
                  <button className="btn btnSm" type="button" onClick={resetChat} disabled={busy || sendingPrompt || aiAssistBlocked}>
                    Reset chat
                  </button>
                  <button
                    className="btn btnSm"
                    type="button"
                    onClick={() => void rebuildChartFromCache()}
                    disabled={busy || sendingPrompt || dirtySourceTransform}
                    title="Refresh visualization using cached data (if available)"
                  >
                    Visualization Refresh
                  </button>
                  <button
                    className="btn btnSm btnPrimary"
                    type="button"
                    onClick={() => void runAndPersist("full")}
                    disabled={runDisabled}
                    title="Run all layers from scratch and persist data"
                  >
                    Full Data Refresh
                  </button>
                </div>
              </div>
              {dirtySourceTransform ? <div className="muted">Save source/transform to use Ask Atlas.</div> : null}
              {runDisabledReason ? <div className="muted">{runDisabledReason}</div> : null}
              <div className="askChatPanel">
                {tenantAiModels.length > 0 ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <select
                      className="pillSelect pillSelectNeutral"
                      value={selectedAiModelId}
                      onChange={(e) => onTenantAiModelChange(e.target.value)}
                      disabled={sendingPrompt || busy || aiAssistBlocked}
                      title="Model for this workspace"
                    >
                      {tenantAiModels.map((m) => (
                        <option key={m.model_id} value={m.model_id}>
                          {m.model_id}
                          {m.status === "deprecated" ? " (deprecated)" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                <div className="askChatMessages" ref={vizChatScrollRef}>
                  {chatMessages.map((item) => (
                    <div key={item.id} className={`askChatMessage askChatMessage${item.role === "user" ? "User" : "Assistant"}`}>
                      {item.text}
                    </div>
                    ))}
                  </div>
                <div className="askChatComposer">
                  <textarea
                    className="input askChatInput"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyDown={(e) =>
                      onAiChatComposerKeyDown(e, {
                        canSubmit:
                          !sendingPrompt && !busy && !dirtySourceTransform && !!prompt.trim() && !aiAssistBlocked,
                        onSubmit: () => void sendAskPrompt()
                      })
                    }
                    rows={2}
                    placeholder=""
                    disabled={aiAssistBlocked}
                  />
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <button
                      className="btn btnPrimary"
                      type="button"
                      onClick={() => void sendAskPrompt()}
                      disabled={sendingPrompt || busy || dirtySourceTransform || prompt.trim() === "" || aiAssistBlocked}
                    >
                      {sendingPrompt ? "Sending..." : aiAssistBlocked ? "Limit…" : "Send"}
                    </button>
                    <SpecSnapshotHistoryButton
                      contextType="report_viz"
                      contextId={reportId}
                      onRestored={(spec) => void handleVizSpecRestore(spec)}
                    />
                  </div>
                </div>
              </div>
              <div className="askConfig">
                <button
                  className="btn btnSm"
                  type="button"
                  onClick={() =>
                    setShowChartConfig((current) => {
                      const next = !current;
                      if (next) {
                        const kind = (savedVlSpec as { kind?: string } | null)?.kind;
                        if (kind === "grid" || kind === "table" || kind === "tantable") {
                          setChartConfigDraft(JSON.stringify(savedVlSpec ?? {}, null, 2));
                          setChartConfigError(null);
                        } else {
                          loadChartConfigDraft(latestAskOption ?? savedVlSpec?.option ?? {});
                        }
                      }
                      return next;
                    })
                  }
                >
                  {showChartConfig ? "Hide visualization config" : "Show visualization config"}
                </button>
                {showChartConfig ? (
                  <>
                    <textarea
                      className="input reportJsonEditor"
                      value={chartConfigDraft}
                      onChange={(e) => {
                        setChartConfigDraft(e.target.value);
                        setChartConfigError(null);
                      }}
                      onBlur={() => void handleChartConfigBlur()}
                      spellCheck={false}
                    />
                    {chartConfigError ? <ErrorState detail={chartConfigError} /> : null}
                  </>
                ) : null}
              </div>
            </div>
          ) : null}
        </section>

        {activeTab !== "metadata" && activeTab !== "source" ? (
        <section className="card reportEditorPreview">
            <div className="reportSectionHeader">
              <h3 className="reportSectionTitle">{activeTab === "transform" ? "Transformed Data" : "Visualization"}</h3>
              {activeTab === "transform" ? (
                <div className="toolbarGroup">
                  <button className="btn btnSm btnPrimary" type="button" onClick={() => void previewStage("transform")} disabled={busy || sendingTransformPrompt || !!transformParse.error}>
                    Run Transformation
                  </button>
                </div>
              ) : null}
            </div>
          <div className="queryMeta">
            {activeTab === "transform" && transformStats ? (
              <>
                {transformStats.runAt ? <span className="pill">Last run: {new Date(transformStats.runAt).toLocaleString()}</span> : null}
                <span className="pill">Duration: {transformStats.durationMs} ms</span>
                {transformStats.rowCount != null ? <span className="pill">Rows: {transformStats.rowCount.toLocaleString()}</span> : null}
              </>
            ) : activeTab === "viz" && vizStats ? (
              <>
                {vizStats.runAt ? <span className="pill">Last run: {new Date(vizStats.runAt).toLocaleString()}</span> : null}
                <span className="pill">Duration: {vizStats.durationMs} ms</span>
                {vizStats.rowCount != null ? <span className="pill">Rows: {vizStats.rowCount.toLocaleString()}</span> : null}
              </>
            ) : (
              <>
                {lastDurationMs != null ? <span className="pill">Total Duration: {lastDurationMs} ms</span> : null}
            {lastRowCount != null ? <span className="pill">Rows: {lastRowCount.toLocaleString()}</span> : null}
              </>
            )}
          </div>

          {activeTab === "transform" && stageErrors.transform ? <ErrorState detail={stageErrors.transform} /> : null}
          {activeTab === "viz" && stageErrors.viz && !chartConfigError ? <ErrorState detail={stageErrors.viz} /> : null}
          {activeTab === "transform" && transformParse.error ? <ErrorState detail={transformParse.error} /> : null}

          {activeTab === "transform" && transformPreview ? (
            <>
              <div style={{ display: "flex", gap: "8px", marginBottom: "16px", borderBottom: "1px solid var(--border)" }}>
                <button
                  type="button"
                  onClick={() => setTransformSubTab("data")}
                  style={{
                    padding: "8px 16px",
                    border: "none",
                    background: transformSubTab === "data" ? "var(--bg-secondary)" : "transparent",
                    borderBottom: transformSubTab === "data" ? "2px solid var(--primary)" : "2px solid transparent",
                    cursor: "pointer",
                    fontWeight: transformSubTab === "data" ? "600" : "400"
                  }}
                >
                  Data
                </button>
                <button
                  type="button"
                  onClick={() => setTransformSubTab("schema")}
                  style={{
                    padding: "8px 16px",
                    border: "none",
                    background: transformSubTab === "schema" ? "var(--bg-secondary)" : "transparent",
                    borderBottom: transformSubTab === "schema" ? "2px solid var(--primary)" : "2px solid transparent",
                    cursor: "pointer",
                    fontWeight: transformSubTab === "schema" ? "600" : "400"
                  }}
                >
                  Schema Contract
                </button>
              </div>
              {transformSubTab === "data" ? (
                <QueryResultsGrid result={transformPreview} hideMeta />
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Column</th>
                      <th>Domain</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transformPreview.columns.map((col) => (
                      <tr key={col.name}>
                        <td>{col.name}</td>
                        <td>{col.type}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          ) : null}
          {activeTab === "viz" && vizPreview?.kind === "echarts" ? (
            <>
              <Chart option={vizPreview.option as EChartsOption} height={vizPreview.height ?? 320} />
              {vizPreview.debugInfo ? (
                <details className="reportPreviewCode" style={{ marginTop: "12px", padding: "12px", fontSize: "11px", maxHeight: "400px", overflow: "auto" }}>
                  <summary style={{ cursor: "pointer", fontWeight: "bold", marginBottom: "8px" }}>Viz Debug Info (TEMP)</summary>
                  <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{JSON.stringify(vizPreview.debugInfo, null, 2)}</pre>
                </details>
          ) : null}
            </>
          ) : null}
          {activeTab === "viz" && vizPreview?.kind === "kpi" ? (
            <KpiCard metrics={vizPreview.metrics} style={vizPreview.style} />
          ) : null}
          {activeTab === "viz" && vizPreview?.kind === "tantable" ? (
            <TanTableViz model={vizPreview} />
          ) : null}
          {(activeTab === "transform" && !transformPreview) || (activeTab === "viz" && !vizPreview) ? (
            <div className="muted">{activeTab === "viz" ? "Not run yet" : "No preview yet for this stage."}</div>
          ) : null}
        </section>
      ) : null}
      </div>
    </PageLayout>
  );
}
